import { getPostSignatureForOssUpload } from '@/api/file'

/**
 * 解析 OSS 错误响应（OSS 返回 XML 格式的错误）
 */
async function parseOssError(response) {
  const text = await response.text()
  const codeMatch = text.match(/<Code>([^<]+)<\/Code>/)
  const msgMatch = text.match(/<Message>([^<]+)<\/Message>/)
  const code = codeMatch ? codeMatch[1] : 'UnknownError'
  const msg = msgMatch ? msgMatch[1] : text
  throw new Error(`OSS上传失败 [${code}]: ${msg}`)
}

/**
 * 上传文件到阿里云OSS（前端直传）
 * 通过后端 getPostSignatureForOssUpload 接口获取临时上传凭证，
 * 再将文件以 PostObject 方式直传至 OSS，返回可访问的文件URL。
 *
 * 预期接口返回数据结构（res.data）：
 * {
 *   ossAccessKeyId: '...',                                   // 临时 AccessKeyId
 *   signature:      '...',                                   // 签名
 *   host:           'https://xxx.oss-cn-xxx.aliyuncs.com',  // OSS 上传地址
 *   dir:            'zbst/zbst/2026/08/18',                  // 存储目录前缀（无尾斜杠）
 *   policy:         '...'                                    // base64 编码的 policy
 * }
 *
 * @param {Blob|File} file     - 文件 Blob/File 对象
 * @param {String}    filename - 文件名（含扩展名）
 * @returns {Promise<String>}  - 上传成功后的文件访问 URL
 */
// 本地开发环境：通过 devServer 代理（/oss-proxy）转发到 OSS，绕开浏览器 CORS；
// 生产环境：直连 OSS host（要求 OSS 桶已配置生产域名的 CORS 规则）。
const PROXY_PATH = '/oss-proxy'
const isDev = process.env.NODE_ENV === 'development'

export async function uploadToOss(file, filename) {
  // 1. 获取 OSS 上传所需参数
  const res = await getPostSignatureForOssUpload()
  const data = res.data || res
  // host 两侧可能带空格/反引号，统一清理
  const ossHost = String(data.host).replace(/[`'"\s]/g, '')
  // 上传用的 URL：开发走代理、生产走直传
  const uploadUrl = isDev ? PROXY_PATH : ossHost
  // dir 统一保证尾部带斜杠，以便直接按 ${dir}${filename} 拼接
  const dir = data.dir.endsWith('/') ? data.dir : `${data.dir}/`
  const { policy, signature, ossAccessKeyId } = data

  // 2. 生成唯一文件名，防止重名覆盖
  const extIndex = filename.lastIndexOf('.')
  const fileExt = extIndex >= 0 ? filename.substring(extIndex) : ''
  const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}${fileExt}`
  const ossKey = `${dir}${uniqueFileName}`

  // 3. 构造 PostObject 表单（file 字段必须放在最后）
  const formData = new FormData()
  formData.append('policy', policy)
  formData.append('OSSAccessKeyId', ossAccessKeyId)
  formData.append('success_action_status', '200')
  formData.append('signature', signature)
  formData.append('key', ossKey)
  formData.append('name', uniqueFileName)
  formData.append('file', file, filename)

  // 4. 上传：开发通过本地代理转发、生产直传 OSS
  let response
  try {
    response = await fetch(uploadUrl, {
      method: 'POST',
      // 本地代理路径是同源，mode 不用指定 cors；直传才显式写 cors（保持一致行为）
      mode: isDev ? 'same-origin' : 'cors',
      body: formData
    })
  } catch (err) {
    if (!isDev && err instanceof TypeError && /Failed to fetch|NetworkError|CORS/i.test(err.message)) {
      throw new Error('OSS上传失败: 可能是CORS跨域被拦截，请检查OSS桶的跨域配置')
    }
    throw err
  }

  if (!response.ok) {
    return parseOssError(response)
  }

  // 5. 返回可访问的文件 URL（必须使用真实 OSS host，不能用本地代理路径，否则生产访问不了）
  return `${ossHost}/${ossKey}`
}
